import dayjs, { Dayjs } from 'dayjs';
import { ColDef, ValueFormatterParams } from 'ag-grid-community';
import { CustomCellRendererProps } from 'ag-grid-react';
import { Checkbox } from 'antd';
import { EditFilled } from '@ant-design/icons';

import NumberEditor from 'components/ag-grid/number-editor';
import DatePickerEditor from 'components/ag-grid/datepicker-editor';
import CheckboxEditor from 'components/ag-grid/checkbox-editor';
import GenderPickerEditor from 'components/ag-grid/gender-picker-editor';
import NationalityPickerEditor from 'components/ag-grid/nationality-picker-editor';
import RoomPickerEditor from 'components/ag-grid/room-picker-editor';
import {
  DATE_TIME_SHORT_FORMAT,
  formatNumber,
  renderWithLineThroughAgTable,
  toLocalTime,
  renderTextWithEllipsis
} from 'utils';
import { BOOKING_STATUS, GROUP_STATUS_NAMES } from 'constants/form';

const RegexDecimal = /\B(?=(\d{3})+(?!\d))/g;

export const getNewGroupCols = (
  arrival: Dayjs,
  departure: Dayjs,
  t: (key: string) => string
): ColDef[] => {
  return [
    {
      field: 'room_type_name',
      headerName: t('agGrid.type'),
      width: 100,
      cellStyle: { textAlign: 'center' },
      headerClass: 'text-center'
    },
    {
      field: 'available_rooms',
      headerName: t('agGrid.avail'),
      width: 100,
      cellStyle: { textAlign: 'center' },
      headerClass: 'text-center'
    },
    {
      field: 'from_date',
      headerName: t('agGrid.fromDate'),
      width: 200,
      cellStyle: { textAlign: 'center' },
      headerClass: 'text-center',
      editable: true,
      cellEditor: DatePickerEditor,
      cellEditorParams: {
        arrival,
        departure
      },
      valueFormatter: (params: ValueFormatterParams) => {
        return dayjs(params.data?.from_date).format('DD/MM/YYYY') || '';
      }
    },
    {
      field: 'to_date',
      headerName: t('agGrid.toDate'),
      width: 200,
      cellStyle: { textAlign: 'center' },
      headerClass: 'text-center',
      editable: true,
      cellEditor: DatePickerEditor,
      cellEditorParams: {
        arrival,
        departure
      },
      valueFormatter: (params: ValueFormatterParams) => {
        return dayjs(params.data?.to_date).format('DD/MM/YYYY') || '';
      }
    },
    {
      field: 'numberOfRooms',
      headerName: t('agGrid.numberOfRooms'),
      width: 100,
      cellStyle: { textAlign: 'center' },
      headerClass: 'text-center',
      editable: true,
      cellEditor: NumberEditor,
      valueFormatter: (params: ValueFormatterParams) => {
        return `${params.data?.numberOfRooms}`.replace(new RegExp(RegexDecimal), ',');
      }
    },
    {
      field: 'pax',
      headerName: t('agGrid.pax'),
      width: 100,
      cellStyle: { textAlign: 'center' },
      headerClass: 'text-center',
      editable: true,
      cellEditor: NumberEditor,
      valueFormatter: (params: ValueFormatterParams) => {
        return `${params.data?.pax}`.replace(new RegExp(RegexDecimal), ',');
      }
    },
    {
      field: 'isRateOverride',
      headerName: t('agGrid.rateOverride'),
      width: 150,
      cellStyle: { textAlign: 'center' },
      headerClass: 'text-center',
      editable: true,
      // singleClickEdit: true,
      cellEditor: CheckboxEditor,
      cellRenderer: (params: CustomCellRendererProps) => {
        const handleChangeOverride = (e: any) => {
          params?.setValue && params.setValue(e.target.checked);
        };
        return <Checkbox checked={params.value} onChange={handleChangeOverride} />;
      }
    },
    {
      field: 'rateAmount',
      headerName: t('agGrid.rateAmount'),
      cellStyle: { textAlign: 'center' },
      headerClass: 'text-center',
      editable: params => {
        return params.data?.isRateOverride;
      },
      cellEditor: NumberEditor,
      valueFormatter: (params: ValueFormatterParams) => {
        return `${params.data?.rateAmount}`.replace(new RegExp(RegexDecimal), ',');
      }
    }
  ];
};

export const getGroupRoomingListCols = (
  startDate: Dayjs,
  endDate: Dayjs,
  t: (key: string) => string
) => {
  const cols: ColDef[] = [
    {
      field: 'seq',
      headerName: t('agGrid.seq'),
      width: 100,
      cellStyle: { textAlign: 'center' },
      headerClass: 'text-center',
      checkboxSelection: true,
      valueGetter: params => {
        return (params.node?.rowIndex || 0) + 1;
      }
    },
    {
      field: 'partner_name',
      headerName: t('agGrid.name'),
      width: 150,
      cellStyle: { textAlign: 'center' },
      headerClass: 'text-center',
      editable: true
    },
    {
      field: 'gender',
      headerName: t('agGrid.gender'),
      width: 120,
      cellStyle: { textAlign: 'center' },
      headerClass: 'text-center',
      editable: true,
      cellEditor: GenderPickerEditor,
      cellRenderer: (params: CustomCellRendererProps) => {
        const handleChangeOverride = (value: any) => {
          params?.setValue && params.setValue(value);
        };
        return <GenderPickerEditor value={params.value} onValueChange={handleChangeOverride} />;
      }
    },
    {
      field: 'dob',
      headerName: t('agGrid.dateBirth'),
      width: 120,
      cellStyle: { textAlign: 'center' },
      headerClass: 'text-center',
      editable: true,
      cellEditor: DatePickerEditor,
      valueFormatter: (params: ValueFormatterParams) => {
        return dayjs(params.data?.dob).format('DD/MM/YYYY') || '';
      }
    },
    {
      field: 'country_id',
      headerName: t('agGrid.nationality'),
      width: 180,
      cellStyle: { textAlign: 'center' },
      headerClass: 'text-center',
      editable: true,
      cellEditor: NationalityPickerEditor,
      cellRenderer: (params: CustomCellRendererProps) => {
        const handleChangeOverride = (value: any) => {
          params?.setValue && params.setValue(value);
        };
        return (
          <NationalityPickerEditor value={params.value} onValueChange={handleChangeOverride} />
        );
      }
    },
    {
      field: 'contact_address_complete',
      headerName: t('agGrid.address'),
      width: 200,
      cellStyle: { textAlign: 'center' },
      headerClass: 'text-center',
      editable: true
    },
    {
      field: 'room_id',
      headerName: t('agGrid.room'),
      width: 160,
      cellStyle: { textAlign: 'center' },
      headerClass: 'text-center',
      editable: true,
      cellEditor: RoomPickerEditor,
      cellEditorParams: {
        startDate,
        endDate
      },
      cellRenderer: (params: CustomCellRendererProps) => {
        const handleChangeOverride = (value: any) => {
          params?.setValue && params.setValue(value);
        };
        return (
          <RoomPickerEditor
            startDate={startDate}
            endDate={endDate}
            value={params.value}
            onValueChange={handleChangeOverride}
          />
        );
      }
    },
    {
      field: 'adult',
      headerName: t('agGrid.adult'),
      width: 80,
      cellStyle: { textAlign: 'center' },
      headerClass: 'text-center',
      editable: true,
      cellEditor: NumberEditor,
      valueFormatter: (params: ValueFormatterParams) => {
        return `${params.data?.adult}`.replace(new RegExp(RegexDecimal), ',');
      }
    },
    {
      field: 'child',
      headerName: t('agGrid.child'),
      width: 80,
      cellStyle: { textAlign: 'center' },
      headerClass: 'text-center',
      editable: true,
      cellEditor: NumberEditor,
      valueFormatter: (params: ValueFormatterParams) => {
        return `${params.data?.child}`.replace(new RegExp(RegexDecimal), ',');
      }
    },
    {
      field: 'phone',
      headerName: t('agGrid.phoneNumber'),
      width: 150,
      cellStyle: { textAlign: 'center' },
      headerClass: 'text-center',
      editable: true
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 150,
      cellStyle: { textAlign: 'center' },
      headerClass: 'text-center',
      editable: true
    }
  ];
  return cols;
};

export const getCashierGuestsColumns = (t: (key: string) => string) => {
  const cashierGuestsColumns: ColDef[] = [
    {
      field: 'status',
      headerName: t('cashierPage.sts'),
      width: 60,
      cellStyle: { textAlign: 'center' },
      headerClass: 'text-center',
      valueFormatter: (params: ValueFormatterParams) => {
        return GROUP_STATUS_NAMES[params?.data?.status as BOOKING_STATUS];
      }
    },
    {
      field: 'booking_id',
      headerName: t('cashierPage.bookingId'),
      width: 70,
      cellStyle: { textAlign: 'center' },
      headerClass: 'text-center'
    },
    {
      field: 'room_no',
      headerName: t('cashierPage.roomNo'),
      width: 115,
      headerClass: 'text-center',
      cellRenderer: (params: ValueFormatterParams) => {
        const data = params?.data;
        return data.is_master_room ? (
          <span>
            {data.room_no || ''}
            <span style={{ fontWeight: 'bold', color: 'blue' }}>MAS.</span>
          </span>
        ) : (
          data.room_no
        );
      }
    },
    {
      field: 'guest_name',
      headerName: t('cashierPage.guestName'),
      minWidth: 190,
      flex: 1,
      headerClass: 'text-center',
      cellRenderer: (params: ValueFormatterParams) => {
        const data = params?.data;
        const displayName = data?.dummy_guest ? `${data.guest_name} (DUMMY)` : data.guest_name;

        return data.main_guest ? (
          <>
            {displayName} <span>*</span>
          </>
        ) : (
          displayName
        );
      }
    },
    {
      field: 'balance',
      headerName: t('cashierPage.balance'),
      width: 110,
      headerClass: 'text-center',
      valueFormatter: (params: ValueFormatterParams) => {
        return formatNumber(params?.data?.balance);
      }
    },
    {
      field: 'check_in',
      headerName: t('cashierPage.checkIn'),
      width: 70,
      headerClass: 'text-center',
      valueFormatter: (params: ValueFormatterParams) => {
        return toLocalTime(params?.data?.check_in, 'DD/MM');
      }
    },
    {
      field: 'check_out',
      headerName: t('cashierPage.checkOut'),
      width: 70,
      headerClass: 'text-center',
      valueFormatter: (params: ValueFormatterParams) => {
        return toLocalTime(params?.data?.check_out, 'DD/MM');
      }
    },
    {
      field: 'booking_line_id',
      headerName: t('cashierPage.bookingLineId'),
      width: 60,
      cellStyle: { textAlign: 'center' },
      headerClass: 'text-center'
    }
  ];

  return cashierGuestsColumns;
};

export const getCashierTransactionCols = (t: (key: string) => string) => {
  const cols: ColDef[] = [
    {
      field: 'transaction_date',
      headerName: t('cashierPage.transactionDate'),
      width: 130,
      cellStyle: { textAlign: 'center' },
      headerClass: 'text-center',
      valueFormatter: (params: ValueFormatterParams) => {
        return dayjs(params.value).format('DD/MM') || '';
      },
      cellRenderer: (params: any) => {
        if (!params.data.is_deleted) {
          return params.value;
        } else {
          return renderWithLineThroughAgTable(params.value, params.data.is_deleted);
        }
      }
    },
    {
      field: 'transaction_code_name',
      headerName: t('cashierPage.transactionCodeName'),
      width: 240,
      cellStyle: { textAlign: 'left' },
      filter: 'agTextColumnFilter',
      headerClass: 'text-left'
    },
    {
      field: 'name',
      headerName: t('cashierPage.transactionDesc'),
      headerClass: 'text-center',
      flex: 1,
      minWidth: 200,
      cellRenderer: (params: any) => {
        if (!params.data.is_deleted) {
          return params.value;
        } else {
          return renderWithLineThroughAgTable(params.value, params.data.is_deleted);
        }
      }
    },
    {
      field: 'amount',
      headerName: t('cashierPage.amount'),
      width: 120,
      cellStyle: { textAlign: 'right' },
      headerClass: 'text-center',
      valueFormatter: (params: ValueFormatterParams) => {
        return formatNumber(params.value);
      },
      cellRenderer: (params: any) => {
        if (!params.data.is_deleted) {
          return formatNumber(params.value);
        } else {
          return renderWithLineThroughAgTable(formatNumber(params.value), params.data.is_deleted);
        }
      }
    },
    {
      field: 'qty',
      headerName: t('cashierPage.qty'),
      width: 80,
      cellStyle: { textAlign: 'right' },
      headerClass: 'text-center',
      valueFormatter: (params: ValueFormatterParams) => {
        return formatNumber(params.value);
      },
      cellRenderer: (params: any) => {
        if (!params.data.is_deleted) {
          return params.value;
        } else {
          return renderWithLineThroughAgTable(params.value, params.data.is_deleted);
        }
      }
    },
    {
      field: 'total',
      headerName: t('cashierPage.total'),
      width: 120,
      cellStyle: { textAlign: 'right' },
      headerClass: 'text-center',
      valueFormatter: (params: ValueFormatterParams) => {
        return formatNumber(params.value);
      },
      cellRenderer: (params: any) => {
        if (!params.data.is_deleted) {
          return formatNumber(params.value);
        } else {
          return renderWithLineThroughAgTable(formatNumber(params.value), params.data.is_deleted);
        }
      }
    },
    {
      field: 'discount_total',
      headerName: t('cashierPage.discountAmount'),
      width: 150,
      valueFormatter: (params: ValueFormatterParams) => {
        return formatNumber(params.value);
      },
      cellRenderer: (params: any) => {
        if (!params.data.is_deleted) {
          return formatNumber(params.value);
        } else {
          return renderWithLineThroughAgTable(formatNumber(params.value), params.data.is_deleted);
        }
      }
    },
    {
      field: 'note',
      headerName: t('common.notice'),
      headerComponent: () => (
        <p
          className="flex items-center m-0 justify-center w-full"
          style={{ color: 'white', fontWeight: 600 }}
        >
          {t('common.notice')}
          <EditFilled style={{ marginLeft: 4 }} />
        </p>
      ),
      width: 160,
      headerClass: 'text-center',
      editable: true,
      cellRenderer: (params: any) => {
        if (!params.data.is_deleted) {
          return params.value;
        } else {
          return renderWithLineThroughAgTable(params.value, params.data.is_deleted);
        }
      }
    },
    {
      field: 'room_no',
      headerName: t('cashierPage.originalRoom'),
      width: 120,
      headerClass: 'text-center',
      cellRenderer: (params: any) => {
        if (!params.data.is_deleted) {
          return params.value;
        } else {
          return renderWithLineThroughAgTable(params.value, params.data.is_deleted);
        }
      }
    },

    {
      field: 'reference',
      headerName: t('cashierPage.ref'),
      width: 150,
      headerClass: 'text-center',
      cellRenderer: (params: any) => {
        if (!params.data.is_deleted) {
          return renderTextWithEllipsis(params.value);
        } else {
          return renderWithLineThroughAgTable(params.value, params.data.is_deleted);
        }
      }
    },

    {
      field: 'created_by',
      headerName: t('cashierPage.createdBy'),
      width: 120,
      cellStyle: { textAlign: 'center' },
      headerClass: 'text-center',
      cellRenderer: (params: any) => {
        if (!params.data.is_deleted) {
          return params.value;
        } else {
          return renderWithLineThroughAgTable(params.value, params.data.is_deleted);
        }
      }
    },

    {
      field: 'created_at',
      headerName: t('cashierPage.createdAt'),
      width: 180,
      cellStyle: { textAlign: 'center' },
      headerClass: 'text-center',
      cellRenderer: (params: any) => {
        if (!params.data.is_deleted) {
          return toLocalTime(params.value, DATE_TIME_SHORT_FORMAT);
        } else {
          return renderWithLineThroughAgTable(
            toLocalTime(params.value, DATE_TIME_SHORT_FORMAT),
            params.data.is_deleted
          );
        }
      }
    },

    {
      field: 'tax_amount',
      headerName: t('cashierPage.taxAmount'),
      width: 100,
      valueFormatter: (params: ValueFormatterParams) => {
        return formatNumber(params.value);
      },
      cellRenderer: (params: any) => {
        if (!params.data.is_deleted) {
          return formatNumber(params.value);
        } else {
          return renderWithLineThroughAgTable(formatNumber(params.value), params.data.is_deleted);
        }
      }
    },

    {
      field: 'service_charge',
      headerName: t('cashierPage.serviceCharge'),
      width: 140,
      valueFormatter: (params: ValueFormatterParams) => {
        return formatNumber(params.value);
      },
      cellRenderer: (params: any) => {
        if (!params.data.is_deleted) {
          return formatNumber(params.value);
        } else {
          return renderWithLineThroughAgTable(formatNumber(params.value), params.data.is_deleted);
        }
      }
    },
    {
      field: 'code',
      headerName: t('cashierPage.transactionCode'),
      filter: 'agTextColumnFilter',
      width: 120,
      cellStyle: { textAlign: 'right' },
      headerClass: 'text-center',
      cellRenderer: (params: any) => {
        if (!params.data.is_deleted) {
          return params.value;
        } else {
          return renderWithLineThroughAgTable(params.value, params.data.is_deleted);
        }
      }
    },
    {
      field: 'id',
      headerName: '#',
      width: 80,
      cellRenderer: (params: any) => {
        if (!params.data.is_deleted) {
          return params.value;
        } else {
          return renderWithLineThroughAgTable(params.value, params.data.is_deleted);
        }
      }
    }
  ];
  return cols;
};
